import { useState } from 'react';
import { Send } from 'lucide-react';
import { FaWhatsapp, FaEnvelope, FaCommentDots } from 'react-icons/fa';
import { useAuth } from '@/contexts/AuthContext';
import { createWhatsAppUrl, CONTACT } from '@/constants/contact';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';
import { useAssistantPanel } from '@/contexts/AssistantContext';

type ContactMethod = 'whatsapp' | 'email' | null;

const ContactSupport = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { open: assistantOpen, panelMounted: assistantMounted, studioChatMounted } = useAssistantPanel();
  const [isOpen, setIsOpen] = useState(false);
  const [contactMethod, setContactMethod] = useState<ContactMethod>(null);
  const [message, setMessage] = useState('');

  // Only show widget if user is logged in. The open AI assistant panel covers
  // the bottom-right corner, so the floating button steps aside meanwhile.
  // AI Studio's chat column fills that corner too.
  if (!user || (assistantOpen && assistantMounted) || studioChatMounted) {
    return null;
  }

  const handleSubmit = () => {
    if (!message.trim() || !contactMethod) {
      return;
    }

    if (contactMethod === 'whatsapp') {
      // Create WhatsApp URL with the user's message
      const whatsappUrl = createWhatsAppUrl(message);
      window.open(whatsappUrl, '_blank');
    } else if (contactMethod === 'email') {
      // Create mailto URL with the user's message
      const mailtoUrl = `mailto:${CONTACT.EMAIL}?subject=${encodeURIComponent(
        t('contactSupport.emailSubject')
      )}&body=${encodeURIComponent(message)}`;
      window.location.href = mailtoUrl;
    }

    // Reset and close dialog
    setMessage('');
    setContactMethod(null);
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setContactMethod(null);
    setMessage('');
  };

  const handleBack = () => {
    setContactMethod(null);
    setMessage('');
  };

  return (
    <>
      {/* Floating contact button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-all hover:bg-green-600 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
        aria-label={t('contact.support')}
      >
        <FaCommentDots size={24} />
      </button>

      {/* Dialog for contact method selection and message input */}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('contact.title')}</DialogTitle>
            <DialogDescription>
              {contactMethod
                ? t('contact.describeIssue')
                : t('contact.chooseMethod')}
            </DialogDescription>
          </DialogHeader>

          {!contactMethod ? (
            /* Contact method selection */
            <div className="grid gap-3 py-4">
              <Button
                type="button"
                variant="outline"
                className="h-auto justify-start gap-5 py-5 px-5"
                onClick={() => setContactMethod('whatsapp')}
              >
                <FaWhatsapp size={80} className="text-green-500 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-semibold text-base">{t('contact.whatsapp')}</div>
                  <div className="text-sm text-muted-foreground">
                    {t('contact.whatsappDescription')}
                  </div>
                </div>
              </Button>

              <Button
                type="button"
                variant="outline"
                className="h-auto justify-start gap-5 py-5 px-5"
                onClick={() => setContactMethod('email')}
              >
                <FaEnvelope size={80} className="text-green-500 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-semibold text-base">{t('contact.email')}</div>
                  <div className="text-sm text-muted-foreground">
                    {t('contact.emailDescription')}
                  </div>
                </div>
              </Button>
            </div>
          ) : (
            /* Message input */
            <div className="py-4">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('contact.placeholder')}
                className="min-h-[150px] resize-none"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleSubmit();
                  }
                }}
              />
            </div>
          )}

          <DialogFooter>
            {contactMethod ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                >
                  {t('common.back')}
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!message.trim()}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {contactMethod === 'whatsapp'
                    ? t('contact.sendWhatsApp')
                    : t('contact.sendEmail')}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                {t('common.cancel')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactSupport;
